import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import feather from 'feather-icons';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import serverClient from '../api/ServerClient';
import { DeleteToken, SetData, SetToken } from '../auth/Auth';
import Loader from '../components/Loader';
import Alert from '../components/Alert';
import SuccessToast from '../toast/SuccessToast';
import { BACKEND_SERVER, NEW_USER_KEY, SERVER_BASE_URL } from '../config/app.config';
import { useLanguage } from '../context/LanguageContext';
import { getPageMeta } from '../util/seoHelper';

const Register = () => {
    const navigate = useNavigate();
    const { t, language, fetchLanguageSettings } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [expandForm, setExpandForm] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState('');

    const openGoogleLogin = (event) => {
        event.preventDefault();
        const width = 500;
        const height = 600;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        window.open(
            `${SERVER_BASE_URL}/auth/google/login`,
            'GoogleLogin',
            `width=${width},height=${height},top=${top},left=${left}`
        );
    };

    // Check password strength
    const checkPasswordStrength = (password) => {
        if (!password) {
            setPasswordStrength('');
            return;
        }

        if (password.length < 6) {
            setPasswordStrength('weak');
        } else if (password.length < 8) {
            setPasswordStrength('medium');
        } else if (
            /[A-Z]/.test(password) &&
            /[0-9]/.test(password) &&
            /[!@#$%^&*]/.test(password)
        ) {
            setPasswordStrength('strong');
        } else {
            setPasswordStrength('medium');
        }
    };

    const openFacebookLogin = (event) => {
        event.preventDefault();
        const width = 500;
        const height = 600;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        window.open(
            `${SERVER_BASE_URL}/auth/facebook/login`,
            'Facebook Login',
            `width=${width},height=${height},top=${top},left=${left}`
        );
    };

    const togglePasswordVisibility = (e) => {
        e.preventDefault();
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = (e) => {
        e.preventDefault();
        setShowConfirmPassword(!showConfirmPassword);
    };

    useEffect(() => {
        feather.replace();
    }, [showPassword, showConfirmPassword]);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        agreeToTerms: false,
    });

    useEffect(() => {
        function handleMessage(event) {
            if (event.origin !== BACKEND_SERVER) {
                return;
            }

            const { error } = event.data;
            if (error) {
                setError(error);
                return;
            }
            const { accessToken, name, isNewUser } = event.data;

            if (accessToken) {
                SetToken(accessToken, name, 3600);
                SetData(NEW_USER_KEY, isNewUser === 'true' || isNewUser === '1');

                // Fetch language settings from API after successful login
                fetchLanguageSettings(true).then(() => {
                    if (isNewUser === 'true' || isNewUser === '1') {
                        navigate('/new-user');
                    } else {
                        navigate('/');
                    }
                });
            }
        }

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [navigate, fetchLanguageSettings]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });

        // Check password strength when password field changes
        if (name === 'password') {
            checkPasswordStrength(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);
            const response = await serverClient.post('/api/auth/signup', formData);
            if (response.status === 201 || response.status === 200) {
                setShowToast(true);
                const accessToken = response?.data?.accessToken;
                const name = response?.data?.username;
                const expiresAt = response?.data?.expiresAt || 3600;
                const isNewUser = true;

                setError('');
                SetToken(accessToken, name, expiresAt);
                SetData(NEW_USER_KEY, isNewUser);

                // Fetch language settings from API after successful signup
                await fetchLanguageSettings(true);

                setTimeout(() => {
                    navigate('/new-user');
                }, 2000);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
            } else if (error.response?.status === 400) {
                setError(error.response?.data?.message);
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-body">
            <Helmet>
                <title>{getPageMeta('register', language).title}</title>
                <meta name="description" content={getPageMeta('register', language).description} />
                <meta name="keywords" content={getPageMeta('register', language).keywords} />
                <link rel="canonical" href={getPageMeta('register', language).url} />

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('register', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('register', language).description}
                />
                <meta property="og:url" content={getPageMeta('register', language).url} />
                <meta property="og:image" content={getPageMeta('register', language).image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={getPageMeta('register', language).title} />
                <meta
                    name="twitter:description"
                    content={getPageMeta('register', language).description}
                />
                <meta name="twitter:image" content={getPageMeta('register', language).image} />
            </Helmet>
            <Loader loading={loading} />
            {/* Header */}
            <div className="auth-header">
                <Link to="/">
                    <i className="back-btn" data-feather="arrow-left"></i>
                </Link>

                <img
                    className="img-fluid img"
                    src={'assets/images/authentication/1.svg'}
                    alt="v1"
                />

                <div className="auth-content">
                    <div>
                        <h2>{t('signUp')}</h2>
                        <h4 className="p-0">{t('signUpSubTitle')}</h4>
                    </div>
                </div>
            </div>

            {/* Form */}
            <form className="auth-form" onSubmit={handleSubmit} target="_blank">
                <div className="custom-container">
                    {error && <Alert alert={'danger'}> {error} </Alert>}
                    <button
                        type="button"
                        onClick={openGoogleLogin}
                        className="btn gray-btn mt-3 width-100"
                    >
                        <img
                            className="img-fluid google"
                            src={'assets/images/svg/google.svg'}
                            alt="google"
                        />
                        {t('continueWithGoogle')}
                    </button>
                    <button
                        type="button"
                        onClick={openFacebookLogin}
                        className="btn gray-btn mt-3 width-100"
                    >
                        <img
                            className="img-fluid google"
                            src={'assets/images/svg/facebook.svg'}
                            alt="facebook"
                        />
                        {t('continueWithFacebook')}
                    </button>

                    <div className="division">
                        <span>{t('or')}</span>
                    </div>

                    {/* Collapsible Form Toggle Button */}
                    <button
                        type="button"
                        onClick={() => setExpandForm(!expandForm)}
                        className="btn gray-btn mt-3 width-100 collapse-toggle-btn"
                    >
                        <span>{expandForm ? '▼' : '▶'}</span>
                        {t('signupWithEmail')}
                    </button>

                    {/* Collapsible Form Section */}
                    {expandForm && (
                        <div className="form-collapse-section">
                            <div className="form-group">
                                <label htmlFor="username" className="form-label">
                                    {t('name')}*
                                </label>
                                <div className="form-input">
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="username"
                                        name="username"
                                        placeholder={t('enterName')}
                                        value={formData.username}
                                        onChange={handleChange}
                                        required={true}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="inputusername" className="form-label">
                                    {t('email')}*
                                </label>
                                <div className="form-input">
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="inputusername"
                                        name="email"
                                        placeholder={t('enterEmail')}
                                        value={formData.email}
                                        required={true}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="password" className="form-label">
                                    {t('password')}*
                                </label>
                                <div className="form-input password-input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-control"
                                        id="password"
                                        name="password"
                                        placeholder={t('enterPassword')}
                                        value={formData.password}
                                        onChange={handleChange}
                                        required={true}
                                    />
                                    <button
                                        type="button"
                                        onClick={togglePasswordVisibility}
                                        className="password-toggle-btn"
                                        aria-label={
                                            showPassword ? 'Hide password' : 'Show password'
                                        }
                                    >
                                        {showPassword ? (
                                            <FiEyeOff className="eye-icon-text-field" />
                                        ) : (
                                            <FiEye className="eye-icon-text-field" />
                                        )}
                                    </button>
                                </div>

                                {/* Password Strength Indicator */}
                                {formData.password && (
                                    <div className="password-strength">
                                        <div className="strength-bar">
                                            <div
                                                className={`strength-fill strength-${passwordStrength}`}
                                            ></div>
                                        </div>
                                        <span
                                            className={`strength-text strength-${passwordStrength}`}
                                        >
                                            {passwordStrength === 'weak' && 'Weak'}
                                            {passwordStrength === 'medium' && 'Medium'}
                                            {passwordStrength === 'strong' && 'Strong'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword" className="form-label">
                                    {t('confirmPassword')}*
                                </label>
                                <div className="form-input password-input-wrapper">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        className="form-control"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        placeholder={t('confirmPassword')}
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required={true}
                                    />
                                    <button
                                        type="button"
                                        onClick={toggleConfirmPasswordVisibility}
                                        className="password-toggle-btn"
                                        aria-label={
                                            showConfirmPassword ? 'Hide password' : 'Show password'
                                        }
                                    >
                                        {showConfirmPassword ? (
                                            <FiEyeOff className="eye-icon-text-field" />
                                        ) : (
                                            <FiEye className="eye-icon-text-field" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="remember-option mt-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        value=""
                                        id="flexCheckDefault"
                                        name="agreeToTerms"
                                        checked={formData.agreeToTerms}
                                        onChange={handleChange}
                                        required={true}
                                    />
                                    <label className="form-check-label" htmlFor="flexCheckDefault">
                                        <Link target="_blank" to={'https://xpenso.xyz/terms'}>
                                            {t('termsConditions')}*
                                        </Link>
                                    </label>
                                </div>
                            </div>

                            <button type="submit" className="btn theme-btn w-100">
                                {t('register')}
                            </button>
                        </div>
                    )}

                    <h4 className="signup">
                        {t('alreadyHaveAccount')} <Link to="/login"> {t('login')}</Link>
                    </h4>
                </div>
            </form>

            {showToast && (
                <SuccessToast message={t('registerSuccess')} onClose={() => setShowToast(false)} />
            )}
        </div>
    );
};

export default Register;
